import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import Certificate from "../../components/Certificate";
import { useAuthContext } from "../../contexts/AuthContext";

export default function CertificatePage() {
  const { courseId } = useParams();
  const { user } = useAuthContext();
  const [course, setCourse] = useState<any>(null);
  const [instructor, setInstructor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    async function checkEligibility() {
      if (!user || !courseId) return;

      try {
        // Load course
        const courseSnap = await getDoc(doc(db, "courses", courseId));
        if (courseSnap.exists()) {
          const courseData = courseSnap.data();
          setCourse({ id: courseSnap.id, ...courseData });

          // Load instructor
          const instructorSnap = await getDoc(doc(db, "users", courseData.instructorId));
          if (instructorSnap.exists()) {
            setInstructor(instructorSnap.data());
          }
        }

        // Check if user completed all lessons
        // TODO: Implement proper completion check
        // For now, assume eligible if user purchased the course
        const purchased = user.purchasedCourses || [];
        setEligible(purchased.includes(courseId));

        setLoading(false);
      } catch (error) {
        console.error("Error checking eligibility:", error);
        setLoading(false);
      }
    }

    checkEligibility();
  }, [user, courseId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-textLight">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!eligible) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-textDark mb-2">
            ยังไม่สามารถรับใบเซอร์ได้
          </h1>
          <p className="text-textLight">
            คุณต้องเรียนจบทุกบทเรียนในคอร์สนี้ก่อน
          </p>
        </div>
      </div>
    );
  }

  if (!course || !instructor) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-textLight">ไม่พบข้อมูลคอร์ส</p>
        </div>
      </div>
    );
  }

  const certificateId = `EDUMI-${courseId?.slice(0, 8).toUpperCase()}-${user?.uid.slice(0, 8).toUpperCase()}`;
  const completionDate = new Date().toLocaleDateString("th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-grayBG py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4 px-4 py-2 bg-gradient-soft rounded-full">
            <span className="text-sm font-semibold bg-gradient-primary bg-clip-text text-transparent">
              🎓 ยินดีด้วย!
            </span>
          </div>
          <h1 className="text-4xl font-bold text-textDark mb-2">
            คุณเรียนจบคอร์สแล้ว
          </h1>
          <p className="text-xl text-textLight">
            ดาวน์โหลดใบประกาศนียบัตรของคุณได้เลย
          </p>
        </div>

        {/* Certificate */}
        <Certificate
          studentName={user?.name || user?.email || "ผู้เรียน"}
          courseName={course.title}
          instructorName={instructor.name || instructor.email}
          completionDate={completionDate}
          certificateId={certificateId}
        />

        {/* Share Section */}
        <div className="text-center mt-12">
          <h3 className="text-xl font-bold text-textDark mb-4">
            แชร์ความสำเร็จของคุณ
          </h3>
          <div className="flex justify-center gap-4">
            <button className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
              แชร์บน Facebook
            </button>
            <button className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors">
              แชร์บน Line
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
